import React, { useEffect, useMemo, useState } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Upload,
  message,
  Row,
  Col,
  Modal,
  Spin,
  // Radio,
  Divider,
} from "antd";
import dayjs from "dayjs";
import { getRequest, putRequest, postRequest } from "../api/api";
import { FaEdit } from "react-icons/fa";
import "./../style/profile.css";

const { Option } = Select;

const Profile = () => {
  const [form] = Form.useForm();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [locLoading, setLocLoading] = useState({
    countries: false,
    states: false,
    cities: false,
  });

  // --- helpers
  const idToName = (list, id) =>
    list.find((x) => x.id === Number(id))?.name ?? "";

  const getRoleLabel = (r) =>
    r === "MA" ? "Main Admin" : r === "A" ? "Admin" : "User";

  const displayLocation = useMemo(() => {
    if (!profileData) return "-";
    const city =
      profileData?.city?.name || idToName(cities, profileData?.city?.name);
    const state =
      profileData?.state?.name || idToName(states, profileData?.state?.name);
    const country =
      profileData?.country?.name ||
      idToName(countries, profileData?.country?.name);
    const arr = [city, state, country].filter(Boolean);
    return arr.length ? arr.join(", ") : "NA";
  }, [profileData, countries, states, cities]);

  // ======= Fetch profile =======
  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await getRequest("profile?ifLocation=true");
      const user = res?.user || {};
      setProfileData(user);

      // preload dependent lists so names resolve quickly
      if (user.country) await fetchStates(user.country);
      if (user.state) await fetchCities(user.state);

      // keep header in sync
      localStorage.setItem("user", JSON.stringify(user));
      window.dispatchEvent(new Event("user-updated"));
    } catch (err) {
      message.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  // ======= Countries / States / Cities =======
  const fetchCountries = async () => {
    try {
      setLocLoading((s) => ({ ...s, countries: true }));
      const res = await getRequest("countries");
      setCountries(res?.data || []);
    } catch {
      message.error("Failed to load countries.");
    } finally {
      setLocLoading((s) => ({ ...s, countries: false }));
    }
  };
  // Helper to extract a primitive id from any shape
  const normalizeId = (v) => {
    if (v == null) return null;
    if (typeof v === "object") return v.value ?? v.id ?? null;
    return v; // already primitive
  };

  const fetchStates = async (countryId) => {
    const cid = normalizeId(countryId);
    if (!cid) {
      setStates([]);
      return;
    }
    try {
      setLocLoading((s) => ({ ...s, states: true }));
      const res = await getRequest(`states/${encodeURIComponent(String(cid))}`);
      setStates(res?.data || []);
    } catch {
      message.error("Failed to load states.");
    } finally {
      setLocLoading((s) => ({ ...s, states: false }));
    }
  };

  const fetchCities = async (stateId) => {
    const sid = normalizeId(stateId);
    if (!sid) {
      setCities([]);
      return;
    }
    try {
      setLocLoading((s) => ({ ...s, cities: true }));
      const res = await getRequest(`cities/${encodeURIComponent(String(sid))}`);
      setCities(res?.data || []);
    } catch {
      message.error("Failed to load cities.");
    } finally {
      setLocLoading((s) => ({ ...s, cities: false }));
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  // Edit modal prefill
  const openEdit = async () => {
    if (!profileData) return;
    setIsEditOpen(true);

    const {
      first_name,
      middle_name,
      last_name,
      email,
      gender,
      mobile_no,
      dob,
      student_or_practitioner,
      current_qualification,
      institution_name,
      any_specialization,
      country,
      state,
      city,
      bio,
    } = profileData;

    // ✅ Get IDs (normalize object or string)
    const countryId = typeof country === "object" ? country.id : country;
    const stateId = typeof state === "object" ? state.id : state;
    const cityId = typeof city === "object" ? city.id : city;

    // ✅ Load dependent dropdowns before setting values
    if (countryId) await fetchStates(countryId);
    if (stateId) await fetchCities(stateId);

    // ✅ Prefill form with correct IDs
    form.setFieldsValue({
      first_name,
      middle_name,
      last_name,
      email,
      gender,
      mobile_no,
      dob: dob ? dayjs(dob) : null,
      student_or_practitioner,
      current_qualification,
      institution_name,
      any_specialization,
      country_id: countryId || undefined,
      state_id: stateId || undefined,
      city_id: cityId || undefined,
      bio,
    });
  };

  // cascades
  const onCountryChange = async (cid) => {
    form.setFieldsValue({ state_id: undefined, city_id: undefined });
    setStates([]);
    setCities([]);
    if (cid) await fetchStates(cid);
  };

  const onStateChange = async (sid) => {
    form.setFieldsValue({ city_id: undefined });
    setCities([]);
    if (sid) await fetchCities(sid);
  };

  // Submit update
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        ...values,
        dob: values.dob ? values.dob.format("YYYY-MM-DD") : null,

        // ✅ Convert numeric IDs to strings
        country: values.country_id ? String(values.country_id) : "",
        state: values.state_id ? String(values.state_id) : "",
        city: values.city_id ? String(values.city_id) : "",
      };
      setLoading(true);
      await putRequest("profile/update", payload);
      message.success("Profile updated successfully");

      await fetchProfile();
      setIsEditOpen(false);

      // Store updated user in localStorage
      const updatedUser = {
        ...JSON.parse(localStorage.getItem("user") || "{}"),
        ...payload,
        country_name: idToName(countries, payload.country),
        state_name: idToName(states, payload.state),
        city_name: idToName(cities, payload.city),
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      window.dispatchEvent(new Event("user-updated"));
    } catch (error) {
      if (error?.response) {
        message.error(error.response?.data?.message || "Update failed");
      } else if (error?.message && !error.errorFields) {
        message.error("Update failed");
      }
    } finally {
      setLoading(false);
    }
  };

  // Upload avatar
  const handleUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append("profile_image", file);

    try {
      setImgUploading(true); // 🔹 show spinner

      await postRequest("users/profile-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const res = await getRequest("profile");
      const user = res?.user || {};
      setProfileData(user);
      localStorage.setItem("user", JSON.stringify(user));
      window.dispatchEvent(new Event("user-updated"));
      message.success("Profile picture updated!");
      onSuccess?.("ok");
    } catch (err) {
      message.error("Image upload failed");
      onError?.(err);
    } finally {
      setImgUploading(false); // 🔹 hide spinner
    }
  };

  const fullName = useMemo(() => {
    if (!profileData) return "";
    return [
      profileData.first_name,
      profileData.middle_name,
      profileData.last_name,
    ]
      .filter(Boolean)
      .join(" ");
  }, [profileData]);

  return (
    <div className="profile-wrap">
      <div className="profile-card">
        <div className="profile-card-header">
          <h3>My Profile</h3>
          <Button type="primary" icon={<FaEdit />} onClick={openEdit}>
            Edit Profile
          </Button>
        </div>

        <Spin spinning={loading}>
          {profileData && (
            <div className="profile-grid">
              {/* LEFT PANEL */}
              <div className="profile-left">
                <Upload customRequest={handleUpload} showUploadList={false}>
                  <div className="profile-avatar">
                    <Spin spinning={imgUploading} size="large">
                      <img
                        src={profileData.profile_image_url || "/user.svg"}
                        alt="avatar"
                        className={imgUploading ? "image-blur" : ""}
                        onError={(e) => {
                          e.currentTarget.src = "/user.svg";
                        }}
                      />
                    </Spin>
                    <span className="profile-avatar-badge">Change</span>
                  </div>
                </Upload>

                <div className="profile-name">{fullName || "-"}</div>
                <div className="profile-role">
                  ({getRoleLabel(profileData.role)})
                </div>
              </div>

              {/* RIGHT PANEL */}
              <div className="profile-right">
                {/* Section: Personal */}
                <div className="section">
                  <div className="section-title">Personal Information</div>

                  <div className="info-row">
                    <div className="info-col">
                      <div className="label">First Name *</div>
                      <div className="value">
                        {profileData.first_name || "NA"}
                      </div>
                    </div>
                    <div className="info-col">
                      <div className="label">Middle Name</div>
                      <div className="value">
                        {profileData.middle_name || "-"}
                      </div>
                    </div>
                  </div>

                  <div className="info-row">
                    <div className="info-col">
                      <div className="label">Last Name</div>
                      <div className="value">
                        {profileData.last_name || "-"}
                      </div>
                    </div>
                    <div className="info-col">
                      <div className="label">Gender *</div>
                      <div className="value">{profileData.gender || "-"}</div>
                    </div>
                  </div>

                  <div className="info-row">
                    <div className="info-col">
                      <div className="label">Date of Birth</div>
                      <div className="value">
                        {profileData.dob
                          ? dayjs(profileData.dob).format("DD / MM / YYYY")
                          : "-"}
                      </div>
                    </div>
                    <div className="info-col">
                      <div className="label">Mobile Number</div>
                      <div className="value">
                        {profileData.mobile_no || "-"}
                      </div>
                    </div>
                  </div>

                  <div className="info-row">
                    <div className="info-col">
                      <div className="label">Email Id</div>
                      <div className="value">{profileData.email || "-"}</div>
                    </div>
                    <div className="info-col">
                      <div className="label">Bio</div>
                      <div className="value">{profileData.bio || "-"}</div>
                    </div>
                  </div>
                </div>

                <Divider className="section-divider" />

                {/* Section: Professional */}
                <div className="section">
                  <div className="section-title">Professional Details</div>

                  <div className="info-row">
                    <div className="info-col">
                      <div className="label">Qualification</div>
                      <div className="value">
                        {profileData.current_qualification || "-"}
                      </div>
                    </div>
                    <div className="info-col">
                      <div className="label">Institution Name</div>
                      <div className="value">
                        {profileData.institution_name || "NA"}
                      </div>
                    </div>
                  </div>

                  <div className="info-row">
                    <div className="info-col">
                      <div className="label">Specialization</div>
                      <div className="value">
                        {profileData.any_specialization || "NA"}
                      </div>
                    </div>
                    {/* <div className="info-col">
                      <div className="label">Student / Practitioner</div>
                      <div className="value">
                        <Radio.Group value={profileData?.student_or_practitioner} disabled>
                          <Radio value="Student">Student</Radio>
                          <Radio value="Practitioner">Practitioner</Radio>
                        </Radio.Group>
                      </div>
                    </div> */}
                  </div>
                </div>

                <Divider className="section-divider" />

                {/* Section: Location */}
                <div className="section">
                  <div className="section-title">Location</div>

                  <div className="info-row">
                    <div className="info-col">
                      <div className="label">Country *</div>
                      <div className="value">
                        {profileData?.country?.name ||
                          idToName(countries, profileData?.country?.name) ||
                          "-"}
                      </div>
                    </div>
                    <div className="info-col">
                      <div className="label">State *</div>
                      <div className="value">
                        {profileData.state?.name ||
                          idToName(states, profileData?.state?.name) ||
                          "-"}
                      </div>
                    </div>
                  </div>

                  <div className="info-row">
                    <div className="info-col">
                      <div className="label">City *</div>
                      <div className="value">
                        {profileData?.city?.name ||
                          idToName(cities, profileData?.city?.name) ||
                          "-"}
                      </div>
                    </div>
                    <div className="info-col">
                      <div className="label">Full Location</div>
                      <div className="value">{displayLocation}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Spin>
      </div>

      {/* ======= Edit Modal (unchanged logic, kept here) ======= */}
      <Modal
        title="Update Profile"
        open={isEditOpen}
        onCancel={() => setIsEditOpen(false)}
        onOk={handleSubmit}
        okText="Save Changes"
        maskClosable={false}
        destroyOnClose
        width={700}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="First Name"
                name="first_name"
                rules={[{ required: true, message: "First name is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Middle Name" name="middle_name">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Last Name"
                name="last_name"
                rules={[{ required: true, message: "Last name is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="Gender" name="gender">
                <Select allowClear>
                  <Option value="Male">Male</Option>
                  <Option value="Female">Female</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="Mobile No" name="mobile_no">
                <Input />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Date of Birth"
                name="dob"
                rules={[
                  {
                    required: true,
                    message: "Please select your Date of Birth",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  disabledDate={(current) =>
                    current && current >= dayjs().endOf("day")
                  }
                  placeholder="Select Date of Birth"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Student/Practitioner"
                name="student_or_practitioner"
              >
                <Select allowClear>
                  <Option value="Student">Student</Option>
                  <Option value="Practitioner">Practitioner</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Current Qualification"
                name="current_qualification"
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="Institution Name" name="institution_name">
                <Input />
              </Form.Item>
            </Col>

            {/* Country / State / City */}
            {/* Country */}
            <Col span={8}>
              <Form.Item label="Country" name="country_id">
                <Select
                  loading={locLoading.countries}
                  placeholder="Select country"
                  onChange={onCountryChange}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {countries.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* State */}
            <Col span={8}>
              <Form.Item label="State" name="state_id">
                <Select
                  loading={locLoading.states}
                  placeholder="Select state"
                  onChange={onStateChange}
                  allowClear
                  disabled={!form.getFieldValue("country_id")}
                  showSearch
                  optionFilterProp="children"
                >
                  {states.map((s) => (
                    <Option key={s.id} value={s.id}>
                      {s.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* City */}
            <Col span={8}>
              <Form.Item label="City" name="city_id">
                <Select
                  loading={locLoading.cities}
                  placeholder="Select city"
                  allowClear
                  disabled={!form.getFieldValue("state_id")}
                  showSearch
                  optionFilterProp="children"
                >
                  {cities.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Any Specialization" name="any_specialization">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Bio" name="bio">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default Profile;
